/**
 * Sticky chrome offsets.
 *
 * Two things inside a page pin themselves to the top of the scroll area: a
 * screen's own header (the campaign editor, the template editor) and sticky
 * side rails. Each of them used `top-0` or a hand-counted `top-20`, so the
 * layers landed on top of each other the moment two of them were on screen.
 *
 * The offsets come from CSS variables declared in `index.css`:
 *
 *   --crm-subheader-h the screen's own pinned header, published at runtime
 *   --crm-nav-top     the top of the content column (settings menu, plain rails)
 *   --crm-rail-top    below the screen's pinned header
 *
 * The app header is NOT in these sums, and that is the whole subtlety here. It
 * is a flex sibling of `.crm-main` rather than an overlay on it, so the scroll
 * container already starts below it and `top: 0` inside that container means
 * "under the app header" on its own. The first version added
 * `--crm-header-h` anyway, which pinned the campaign header 74px too low and
 * left a hole at the top of the page for the readiness strip to scroll through.
 *
 * A screen header is as tall as its content - it wraps on a narrow window and
 * grows by a line when there is unsaved work - so it cannot be a constant. It
 * measures itself with `usePinnedHeaderHeight` and publishes the number; a rail
 * that has to clear it reads `--crm-rail-top` and never needs to know why.
 */
import { useLayoutEffect, type RefObject } from "react";

/** Where a screen's own pinned header sticks: the top of the content column,
 *  which is already the line just under the app header. */
export const PINNED_HEADER_TOP = "sticky top-0 z-10";

/**
 * Publishes the element's height as `--crm-subheader-h` for as long as it is
 * mounted. The variable goes on the document root because the readers are in a
 * different subtree (a side rail inside a tab panel), and it is removed on
 * unmount so the next screen does not inherit an offset for a header that is
 * no longer there.
 */
export function usePinnedHeaderHeight(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const write = () => root.style.setProperty("--crm-subheader-h", `${el.offsetHeight}px`);
    write();
    const ro = new ResizeObserver(write);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--crm-subheader-h");
    };
  }, [ref]);
}
